/*
 This file is part of GNU Taler
 (C) 2021-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 *
 * @author Sebastian Javier Marchano (sebasjm)
 */

import {
  AccessToken,
  Amounts,
  assertUnreachable,
  HttpStatusCode,
  TalerMerchantApi,
} from "@gnu-taler/taler-util";
import {
  ButtonBetterBulma,
  LocalNotificationBannerBulma,
  RenderAmountBulma,
  useLocalNotificationBetter,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { format } from "date-fns";
import { Fragment, h, VNode } from "preact";
import { useSessionContext } from "../../../../context/session.js";
import { WithId } from "../../../../declaration.js";
import {
  datetimeFormatForPreferences,
  usePreference,
} from "../../../../hooks/preference.js";
import { Tooltip } from "../../../../components/Tooltip.js";
import { revalidateInstanceIncomingTransfers } from "../../../../hooks/transfer.js";

const TALER_SCREEN_ID = 72;

interface TablePropsIncoming {
  transfers: (TalerMerchantApi.ExpectedTransferEntry & WithId)[];
  onLoadMoreBefore?: () => void;
  onLoadMoreAfter?: () => void;
  onDetails: (d: number) => void;
}

export function CardTableIncoming({
  transfers,
  onLoadMoreAfter,
  onLoadMoreBefore,
  onDetails,
}: TablePropsIncoming): VNode {
  const { i18n } = useTranslationContext();
  const [preferences] = usePreference();
  const { config } = useSessionContext();
  const { state: session, lib } = useSessionContext();

  const [notification, safeFunctionHandler] = useLocalNotificationBetter();

  const confirm = safeFunctionHandler(
    i18n.str`inform wire transfer`,
    (at: AccessToken, wt: TalerMerchantApi.ExpectedTransferEntry) =>
      lib.instance.informWireTransfer(at, {
        credit_amount: wt.expected_credit_amount!,
        exchange_url: wt.exchange_url,
        payto_uri: wt.payto_uri,
        wtid: wt.wtid,
      }),
  );
  confirm.onSuccess = () => {
    revalidateInstanceIncomingTransfers();
  };
  confirm.onFail = (fail) => {
    switch (fail.case) {
      case HttpStatusCode.Unauthorized:
        return i18n.str`Unauthorized.`;
      case HttpStatusCode.NotFound:
        return i18n.str`Not found.`;
      case HttpStatusCode.Conflict:
        return i18n.str`Wire transfer already confirmed.`;
      default:
        assertUnreachable(fail);
    }
  };
  return (
    <Fragment>
      <LocalNotificationBannerBulma notification={notification} />

      <div class="card has-table">
        <header class="card-header">
          <p class="card-header-title">
            <i class="icon mdi mdi-star" />
            <i18n.Translate>New wire transfers</i18n.Translate>
          </p>
        </header>
        <div class="card-content">
          <div class=" has-pagination">
            <div class="table-wrapper has-mobile-cards">
              {transfers.length > 0 ? (
                <div class="">
                  {onLoadMoreBefore && (
                    <Tooltip
                      text={i18n.str`Load more wire transfers preceding the first one`}
                    >
                      <button
                        type="button"
                        class="button is-fullwidth"
                        onClick={onLoadMoreBefore}
                      >
                        <i18n.Translate>Load first page</i18n.Translate>
                      </button>
                    </Tooltip>
                  )}
                  <table class="table is-fullwidth is-striped is-hoverable">
                    <thead>
                      <tr>
                        <th>
                          <i18n.Translate>Date</i18n.Translate>
                        </th>
                        <th>
                          <i18n.Translate>Amount</i18n.Translate>
                        </th>
                        <th>
                          <i18n.Translate>ID</i18n.Translate>
                        </th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {transfers.map((i, idx) => {
                        return (
                          <tr key={i.id}>
                            <td
                              onClick={(): void =>
                                onDetails(i.expected_transfer_serial_id!)
                              }
                              style={{ cursor: "pointer" }}
                            >
                              {i.execution_time
                                ? i.execution_time.t_s == "never"
                                  ? i18n.str`never`
                                  : format(
                                      i.execution_time.t_s * 1000,
                                      datetimeFormatForPreferences(preferences),
                                    )
                                : i18n.str`unknown`}
                            </td>
                            <td
                              onClick={(): void =>
                                onDetails(i.expected_transfer_serial_id!)
                              }
                              style={{ cursor: "pointer" }}
                            >
                              {i.expected_credit_amount ? (
                                <RenderAmountBulma
                                  value={Amounts.parseOrThrow(
                                    i.expected_credit_amount,
                                  )}
                                  specMap={config.currencies}
                                />
                              ) : (
                                <span>
                                  <i18n.Translate>
                                    To be determined.
                                  </i18n.Translate>
                                </span>
                              )}
                            </td>
                            <td
                              title={i.wtid}
                              onClick={(): void =>
                                onDetails(i.expected_transfer_serial_id!)
                              }
                              style={{ cursor: "pointer" }}
                            >
                              {i.wtid}
                            </td>
                            <td class="is-actions-cell right-sticky">
                              {!i.expected_credit_amount ? undefined : (
                                <div class="buttons is-right">
                                  <Tooltip
                                    text={i18n.str`You confirm that the incoming wire transfer has arrived into your bank account.`}
                                  >
                                    <ButtonBetterBulma
                                      class="button is-success is-small"
                                      onClick={
                                        !session.token
                                          ? undefined
                                          : confirm.withArgs(session.token, i)
                                      }
                                      type="button"
                                    >
                                      Confirm
                                    </ButtonBetterBulma>
                                  </Tooltip>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {onLoadMoreAfter && (
                    <Tooltip
                      text={i18n.str`Load more transfers after the last one`}
                    >
                      <button
                        type="button"
                        class="button is-fullwidth"
                        onClick={onLoadMoreAfter}
                      >
                        <i18n.Translate>Load next page</i18n.Translate>
                      </button>
                    </Tooltip>
                  )}
                </div>
              ) : (
                <EmptyTable />
              )}
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
}

interface TablePropsVerified {
  transfers: (TalerMerchantApi.TransferDetails & WithId)[];
  onLoadMoreBefore?: () => void;
  onLoadMoreAfter?: () => void;
  onDetails: (d: number) => void;
}

export function CardTableVerified({
  transfers,
  onLoadMoreAfter,
  onLoadMoreBefore,
  onDetails,
}: TablePropsVerified): VNode {
  const { i18n } = useTranslationContext();
  const { config } = useSessionContext();
  const [preferences] = usePreference();
  return (
    <div class="card has-table">
      <header class="card-header">
        <p class="card-header-title">
          <i class="icon mdi mdi-arrow-left-right" />
          <i18n.Translate>
            Confirmed wire transfers into bank account
          </i18n.Translate>
        </p>
      </header>
      <div class="card-content">
        <div class=" has-pagination">
          <div class="table-wrapper has-mobile-cards">
            {transfers.length > 0 ? (
              <div class="">
                {onLoadMoreBefore && (
                  <Tooltip
                    text={i18n.str`Load more wire transfers preceding the first one`}
                  >
                    <button
                      type="button"
                      class="button is-fullwidth"
                      onClick={onLoadMoreBefore}
                    >
                      <i18n.Translate>Load first page</i18n.Translate>
                    </button>
                  </Tooltip>
                )}
                <table class="table is-fullwidth is-striped is-hoverable">
                  <thead>
                    <tr>
                      <th>
                        <i18n.Translate>Date</i18n.Translate>
                      </th>
                      <th>
                        <i18n.Translate>Amount</i18n.Translate>
                      </th>
                      <th>
                        <i18n.Translate>ID</i18n.Translate>
                      </th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {transfers.map((i) => {
                      return (
                        <tr key={i.id}>
                          <td>
                            {i.execution_time
                              ? i.execution_time.t_s == "never"
                                ? i18n.str`never`
                                : format(
                                    i.execution_time.t_s * 1000,
                                    datetimeFormatForPreferences(preferences),
                                  )
                              : i18n.str`unknown`}
                          </td>
                          <td>
                            <RenderAmountBulma
                              value={Amounts.parseOrThrow(i.credit_amount)}
                              specMap={config.currencies}
                            />
                          </td>
                          <td
                            title={i.wtid}
                            style={{ wordBreak: "break-word" }}
                          >
                            {i.wtid}
                          </td>
                          <td>
                            <div class="buttons is-right">
                              <Tooltip
                                text={i18n.str`Show details about the incoming wire transfer.`}
                              >
                                <button
                                  class="button is-info is-small"
                                  onClick={() => {
                                    onDetails(i.transfer_serial_id);
                                  }}
                                  type="button"
                                >
                                  <i18n.Translate>Details</i18n.Translate>
                                </button>
                              </Tooltip>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {onLoadMoreAfter && (
                  <Tooltip
                    text={i18n.str`Load more transfers after the last one`}
                  >
                    <button
                      type="button"
                      class="button is-fullwidth"
                      onClick={onLoadMoreAfter}
                    >
                      <i18n.Translate>Load next page</i18n.Translate>
                    </button>
                  </Tooltip>
                )}
              </div>
            ) : (
              <EmptyTable />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyTable(): VNode {
  const { i18n } = useTranslationContext();
  return (
    <div class="content has-text-grey has-text-centered">
      <p>
        <span class="icon is-large">
          <i class="icon mdi mdi-magnify mdi-48px" />
        </span>
      </p>
      <p>
        <i18n.Translate>
          There are no transfers to list yet, add more by pressing the + sign
        </i18n.Translate>
      </p>
    </div>
  );
}
